import { useCallback, useEffect, useRef, useState } from "react";
import { fetchRoomViewersService } from "../services/modelsService";

/** Cada cuánto se vuelve a preguntar el aforo. */
const DEFAULT_INTERVAL = 25000;

/**
 * Aforo en vivo de un grupo de salas.
 *
 * Dentro de una sala el número sale del canal de presencia, que el navegador
 * ya tiene abierto; para un listado eso significaría abrir doce sockets, así
 * que aquí se pregunta por HTTP y se refresca cada pocos segundos.
 *
 * Mientras no se sepa el aforo de una sala, se devuelve `undefined` en vez de
 * cero: la interfaz debe callarse, no afirmar que no hay nadie.
 *
 * @param {number[]} ids      Salas a vigilar.
 * @param {number}   interval Milisegundos entre consultas.
 */
export const useRoomViewers = (ids = [], interval = DEFAULT_INTERVAL) => {
    const [viewers, setViewers] = useState({});
    const [available, setAvailable] = useState(true);

    // La lista de ids cambia de identidad en cada render aunque su contenido
    // sea el mismo; se compara por valor para no reprogramar el intervalo (ni
    // disparar una consulta) en cada repintado del listado.
    const key = ids.join(',');
    const idsRef = useRef(ids);
    idsRef.current = ids;

    const load = useCallback(async () => {
        if (!idsRef.current.length) return;

        try {
            const data = await fetchRoomViewersService(idsRef.current);
            setAvailable(data.available !== false);
            setViewers(data.viewers || {});
        } catch (error) {
            // Sin aforo el listado sigue siendo perfectamente utilizable.
            setAvailable(false);
        }
    }, []);

    useEffect(() => {
        if (!key) {
            setViewers({});
            return undefined;
        }

        load();

        const timer = setInterval(load, interval);
        return () => clearInterval(timer);
    }, [key, interval, load]);

    /** Aforo de una sala, o undefined mientras no se sepa. */
    const countFor = useCallback((id) => (
        available ? viewers[id] : undefined
    ), [available, viewers]);

    return { viewers, available, countFor, refresh: load };
};
